import { useState } from "react";
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import { AccountPanel } from "./AccountPanel";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { NAV_ROW_HEIGHT } from "./layout";

/**
 * The account control for the tablet rail and desktop sidebar (spec §7). The
 * same account section lives in the phone burger sheet; all entries reuse the
 * existing theme/language/login logic through {@link AccountPanel}.
 */
export function ProfileMenu({
  variant,
  showLabel = true,
}: {
  /** Rail/icon-only avatar, or the full sidebar row. */
  variant: "avatar" | "row";
  showLabel?: boolean;
}) {
  const t = useT();
  const auth = useAppStore((s) => s.auth);
  const user = useAppStore((s) => s.user);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (auth !== "authenticated") return null;

  const name = user?.displayName ?? user?.sub ?? t("profile");
  const initial = name[0]?.toUpperCase() ?? "?";
  const trigger =
    variant === "avatar" || !showLabel ? (
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={t("profile")}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="account-menu"
        sx={{ width: 44, height: 44 }}
      >
        <Avatar sx={{ width: 32, height: 32, fontSize: 14, fontWeight: 700, bgcolor: "primary.main" }}>
          {initial}
        </Avatar>
      </IconButton>
    ) : (
      <ButtonBase
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={t("profile")}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="account-menu"
        sx={{
          width: "100%",
          height: NAV_ROW_HEIGHT,
          justifyContent: "flex-start",
          gap: 1.5,
          px: 2,
          color: "text.secondary",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Avatar sx={{ width: 32, height: 32, fontSize: 14, fontWeight: 700, bgcolor: "primary.main" }}>
          {initial}
        </Avatar>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      </ButtonBase>
    );

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <AccountPanel onNavigate={() => setAnchorEl(null)} />
      </Popover>
    </>
  );
}
